import mustache from "mustache";
import { BaseRequest } from "../../../../base/request/BaseRequest";
import { FormField } from "../../../base/form/FormField";
import { TextConfig } from "../../../definitions/form/inputs/config/TextConfig";

import template from "./template.html";
import { TextValidation } from "../../../definitions/form/inputs/validation/TextValidation";
import { ITextField } from "../../../definitions/form/inputs/ITextField";

mustache.parse(template);

export class TextField<TRequest extends BaseRequest>
  extends FormField<TextConfig<TRequest>>
  implements ITextField
{
  private readonly _request: TRequest;
  private readonly _validation: TextValidation;

  constructor(
    request: TRequest,
    config: TextConfig<TRequest>,
    validation: TextValidation,
  ) {
    super(config);
    this._request = request;
    this._validation = validation;
  }

  private _onChange?: () => void;

  setOnChange(handler: () => void): void {
    this._onChange = handler;
  }

  getTemplate(): string {
    return template;
  }

  getData(): unknown {
    const isRequired = this._validation.isRequired ?? false;
    const data = {
      ...this._config,
      label: `${this._config.label}${isRequired ? " *" : ""}`,
      hasGroup: !!(this._config.icon || this._config.type === "password"),
      placeholder: this._config.placeholder ?? "",
      autocomplete: this._config.autocomplete ?? "off",
      value: this._getRequestValue(),
      id: this._formFieldId,
    };
    return data;
  }

  connect(): void {
    this._input = document.getElementById(
      `input-${this._formFieldId}`,
    ) as HTMLInputElement;
    this._input?.addEventListener("input", () => {
      this._updateValue();
    });

    this._error = document.getElementById(`error-${this._formFieldId}`);
  }

  doDispose(): void {}

  getError(): string | null {
    const value = this._getRequestValue();

    if (this._validation.isRequired && value.trim().length == 0)
      return `Ingrese ${this._config.label}`;
    if (this._validation.minLength && value.length < this._validation.minLength)
      return `Minimo ${this._validation.minLength} caracteres`;
    if (this._validation.maxLength && value.length > this._validation.maxLength)
      return `Maximo ${this._validation.maxLength} caracteres`;
    if (
      this._config.type === "email" &&
      value.length > 0 &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    )
      return "Ingrese un email válido";

    // Última: las reglas propias del campo ya pasaron (mismo orden que
    // `InputNumberField.getError`).
    if (this._validation.custom) return this._validation.custom(value);

    return null;
  }

  showError(): void {
    const error = this.getError();
    if (this._error) this._error.innerHTML = error ?? "";
  }

  focus(): void {
    this._input?.focus();
  }

  disabled(): void {
    if (this._input) this._input.disabled = true;
  }

  enabled(): void {
    if (this._input) this._input.disabled = false;
  }

  private _input: HTMLInputElement | null = null;
  private _error: HTMLSpanElement | null = null;

  private _getRequestValue = (): string => {
    return this._config.get(this._request);
  };

  private _updateValue = () => {
    if (!this._input) return;

    let value = this._input.value;
    if (this._config.uppercase) {
      const { selectionStart, selectionEnd } = this._input;
      value = value.toUpperCase();
      this._input.value = value;
      this._input.setSelectionRange(selectionStart, selectionEnd);
    }

    this._config.set(this._request, value);
    if (this._onChange) this._onChange();
    this.showError();
  };
}
